/**
 * TeamWeeklyBoard.jsx — Phase 84.10 (Team swim-lanes)
 *
 * One 7-day Kanban board per team member on the People > Team-board tab.
 * Each row: sticky left cell (avatar + name + BU) + 7 day columns (Mon–Sun)
 * containing that person's daily tasks filtered by due_date. Drag-and-drop
 * (mouse / touch / keyboard via @dnd-kit) reassigns a task to a different
 * day within the same person's row.
 *
 * The board reuses <KanbanCard /> and the day-key math (mondayOf,
 * toISODateLocal, DAY_LABELS) from DailyTasksWidget so both surfaces stay
 * behaviourally identical.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Eye, EyeOff } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
  KanbanCard, DAY_LABELS, UNSCHEDULED_KEY, mondayOf, toISODateLocal,
  dashboardCollisionDetection,
} from "@/components/DailyTasksWidget";

// Compound droppable id "userId::dateKey" so a drop knows both target
// person and target day. The dnd-kit collision detection then only fires
// for cells in the target person's row.
const cellId = (uid, dateKey) => `${uid}::${dateKey}`;
const parseCellId = (id) => {
  const s = String(id || "");
  const idx = s.indexOf("::");
  return idx === -1 ? { userId: null, dateKey: s } : { userId: s.slice(0, idx), dateKey: s.slice(idx + 2) };
};

export default function TeamWeeklyBoard() {
  const [users, setUsers] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeTaskId, setActiveTaskId] = useState(null);
  // Phase 87 — Done tasks are hidden by default to cut clutter; toggle reveals them.
  const [showDone, setShowDone] = useState(false);

  const monday = useMemo(() => {
    const m = mondayOf(new Date());
    m.setDate(m.getDate() + weekOffset * 7);
    return m;
  }, [weekOffset]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
    }),
    [monday],
  );
  const todayISO = toISODateLocal(new Date());

  const load = useCallback(async () => {
    try {
      const [uRes, tRes] = await Promise.all([
        api.get("/users?kind=internal"),
        // Fetch every internal user's daily tasks. Filtering to the visible
        // week is done client-side in byUserDay below.
        api.get("/daily-tasks?assignee_id=all"),
      ]);
      // Only show active team members; sort by name for stable rows.
      const activeUsers = (uRes.data || [])
        .filter((u) => (u.status || "active") === "active" && u.role !== "client")
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setUsers(activeUsers);
      setTasks(tRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setUsers([]); setTasks([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group tasks by assignee_id → dateKey ('YYYY-MM-DD' | UNSCHEDULED_KEY).
  const byUserDay = useMemo(() => {
    const m = new Map();
    for (const t of (tasks || [])) {
      const uid = t.assignee_id;
      if (!uid) continue;
      if (!m.has(uid)) m.set(uid, {});
      const bucket = m.get(uid);
      const iso = t.due_date && /^\d{4}-\d{2}-\d{2}/.test(t.due_date) ? t.due_date.slice(0, 10) : null;
      const key = iso || UNSCHEDULED_KEY;
      (bucket[key] = bucket[key] || []).push(t);
    }
    return m;
  }, [tasks]);

  // Phase 87 — the board view shown to swim lanes / visibility math: Done
  // tasks are filtered out unless `showDone` is toggled on.
  const visibleByUserDay = useMemo(() => {
    if (showDone) return byUserDay;
    const m = new Map();
    for (const [uid, buckets] of byUserDay.entries()) {
      const filtered = {};
      for (const [key, arr] of Object.entries(buckets)) {
        const kept = (arr || []).filter((t) => t.status !== "done");
        if (kept.length) filtered[key] = kept;
      }
      m.set(uid, filtered);
    }
    return m;
  }, [byUserDay, showDone]);

  const weekRangeLabel = useMemo(() => {
    const opts = { day: "numeric", month: "short" };
    return `${days[0].toLocaleDateString(undefined, opts)} – ${days[6].toLocaleDateString(undefined, { ...opts, year: "numeric" })}`;
  }, [days]);

  // Feb 2026 · fix — Team Kanban board no longer shows lanes for teammates
  // who have zero tasks in the currently visible week. The old behaviour
  // rendered every active internal user (~20+ empty lanes) which made the
  // board noisy and hid the actual workload. A lane surfaces only if the
  // user has ≥1 task in either the Unscheduled bucket OR any of the 7
  // visible days (Mon–Sun of the current `weekOffset` view). Navigating
  // between weeks re-evaluates visibility, so an empty week correctly
  // shows a "no assignees this week" empty state instead of a wall of
  // ghost rows.
  const visibleUsers = useMemo(() => {
    if (!users) return null;
    const weekIsos = new Set(days.map((d) => toISODateLocal(d)));
    weekIsos.add(UNSCHEDULED_KEY);
    return users.filter((u) => {
      const buckets = visibleByUserDay.get(u.id) || {};
      return Object.entries(buckets).some(([key, arr]) => weekIsos.has(key) && (arr?.length || 0) > 0);
    });
  }, [users, visibleByUserDay, days]);

  // Optimistic PATCH — mirror the DailyTasksWidget setRowDueDate helper so
  // the swim-lane reacts instantly and syncs on the backend afterwards.
  const setDueDate = async (task, newDate) => {
    const patch = { due_date: newDate || "" };
    setTasks((arr) => arr.map((x) => x.id === task.id ? { ...x, due_date: newDate || null } : x));
    try {
      const { data } = await api.patch(`/daily-tasks/${task.id}`, patch);
      setTasks((arr) => arr.map((x) => x.id === task.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); load(); /* re-fetch on failure */ }
  };
  const setStatus = async (task, next) => {
    setTasks((arr) => arr.map((x) => x.id === task.id ? { ...x, status: next } : x));
    try {
      const { data } = await api.patch(`/daily-tasks/${task.id}`, { status: next });
      setTasks((arr) => arr.map((x) => x.id === task.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (task) => {
    const next = task.status === "done" ? (task.previous_status || "planned") : "done";
    setStatus(task, next);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeTask = activeTaskId ? (tasks || []).find((t) => t.id === activeTaskId) : null;

  const handleDragEnd = (e) => {
    setActiveTaskId(null);
    const taskId = e.active?.id;
    const overId = e.over?.id;
    if (!taskId || !overId) return;
    const task = (tasks || []).find((t) => t.id === taskId);
    if (!task) return;
    const { userId, dateKey } = parseCellId(overId);
    // Guardrail: only accept drops back into the same person's row. Cross-
    // person reassignment is intentionally NOT wired here to keep the
    // interaction unambiguous (managers use the DailyTasksWidget's row-level
    // Reassignee dropdown for cross-person moves).
    if (userId && userId !== task.assignee_id) {
      toast.info("Drag within a person's row to change day. Use row menu to reassign to another team member.");
      return;
    }
    const newDate = dateKey === UNSCHEDULED_KEY ? "" : dateKey;
    const currentIso = task.due_date && /^\d{4}-\d{2}-\d{2}/.test(task.due_date) ? task.due_date.slice(0, 10) : "";
    if (currentIso === newDate) return;
    setDueDate(task, newDate);
  };

  return (
    <div className="p-4 sm:p-6 fade-in" data-testid="team-weekly-board">
      <PageHeader
        eyebrow="Team"
        title="Team board · this week"
        subtitle="Only teammates with tasks scheduled this week appear here. Drag a card to change its day. Use Prev/Next to browse other weeks."
        actions={
          <div className="flex items-center gap-1">
            <button onClick={() => setWeekOffset(weekOffset - 1)} className="p-1 rounded-sm hover:bg-slate-100" data-testid="team-board-prev" title="Previous week">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setWeekOffset(0)} className="text-[11px] px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200" data-testid="team-board-today" title="Jump to this week">
              {weekOffset === 0 ? "This week" : "Today"}
            </button>
            <button onClick={() => setWeekOffset(weekOffset + 1)} className="p-1 rounded-sm hover:bg-slate-100" data-testid="team-board-next" title="Next week">
              <ChevronRight size={16} />
            </button>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium ml-3" data-testid="team-board-range">{weekRangeLabel}</div>
            <button
              onClick={() => setShowDone((v) => !v)}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm border ml-3 ${showDone ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
              data-testid="team-board-toggle-done"
              title={showDone ? "Hide done tasks" : "Show done tasks"}
            >
              {showDone ? <Eye size={12} /> : <EyeOff size={12} />} Done
            </button>
          </div>
        }
      />

      {users === null || tasks === null ? (
        <div className="text-xs text-slate-400 italic py-8 text-center">Loading team board…</div>
      ) : users.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-8 text-center flex flex-col items-center gap-2">
          <Users size={20} className="text-slate-300" /> No active team members.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={dashboardCollisionDetection}
          onDragStart={(e) => setActiveTaskId(e.active?.id)}
          onDragEnd={handleDragEnd}
        >
          {/* Column headers — one date header row spans all swim lanes. */}
          <div className="grid grid-cols-[180px_1fr] mb-1 gap-x-2 sticky top-0 bg-white z-10 pt-1" data-testid="team-board-header">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 pl-1">Team member</div>
            <div className="grid grid-cols-8 gap-1">
              <DayHeader label="Unsched." sub="No date" isToday={false} />
              {days.map((d, i) => {
                const iso = toISODateLocal(d);
                return (
                  <DayHeader
                    key={iso}
                    label={DAY_LABELS[i]}
                    sub={d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                    isToday={iso === todayISO}
                  />
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            {visibleUsers.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-8 text-center flex flex-col items-center gap-2" data-testid="team-board-empty-week">
                <Users size={20} className="text-slate-300" />
                No teammates have tasks scheduled for this week.
                {tasks.length > 0 && (
                  <span className="text-[10px] text-slate-400">
                    Empty lanes are hidden automatically — use Prev/Next to browse other weeks.
                  </span>
                )}
              </div>
            ) : (
              visibleUsers.map((u) => (
                <SwimLane
                  key={u.id}
                  user={u}
                  days={days}
                  todayISO={todayISO}
                  buckets={visibleByUserDay.get(u.id) || {}}
                  onStatusChange={setStatus}
                  onToggle={toggle}
                />
              ))
            )}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeTask ? <KanbanCard task={activeTask} showAssignee={false} onStatusChange={() => {}} onToggle={() => {}} isOverlay /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <div className="mt-3 text-[10px] text-slate-400 italic">
        Tip · drag within a person&rsquo;s row to change the task&rsquo;s due date. Keyboard: focus the drag handle → Space → arrows → Space.
      </div>
    </div>
  );
}

function DayHeader({ label, sub, isToday }) {
  return (
    <div className={`px-2 py-1 rounded-sm border ${isToday ? "border-[#F54800] bg-orange-50" : "border-transparent"}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${isToday ? "text-[#F54800]" : "text-slate-700"}`}>{label}</div>
      <div className="text-[9px] text-slate-500">{sub}</div>
    </div>
  );
}

function SwimLane({ user, days, todayISO, buckets, onStatusChange, onToggle }) {
  const total = Object.values(buckets).reduce((n, arr) => n + (arr?.length || 0), 0);
  return (
    <div className="grid grid-cols-[180px_1fr] gap-x-2 border-b border-slate-100 pb-2" data-testid={`team-board-lane-${user.id}`}>
      <div className="pl-1 pt-1 flex items-start gap-2 sticky left-0">
        <div className="w-7 h-7 rounded-sm bg-slate-200 text-slate-600 text-[10px] font-semibold uppercase flex items-center justify-center shrink-0 overflow-hidden">
          {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : (user.name || "?").slice(0, 2)}
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium text-slate-900 truncate" title={user.name}>{user.name}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {user.role || "internal"}{user.business_unit ? ` · ${user.business_unit}` : ""}
          </div>
          <div className="text-[9px] text-slate-400" data-testid={`team-board-total-${user.id}`}>{total} task{total === 1 ? "" : "s"}</div>
        </div>
      </div>
      <div className="grid grid-cols-8 gap-1">
        <LaneCell
          userId={user.id}
          dateKey={UNSCHEDULED_KEY}
          isToday={false}
          tasks={buckets[UNSCHEDULED_KEY] || []}
          onStatusChange={onStatusChange}
          onToggle={onToggle}
        />
        {days.map((d) => {
          const iso = toISODateLocal(d);
          return (
            <LaneCell
              key={iso}
              userId={user.id}
              dateKey={iso}
              isToday={iso === todayISO}
              tasks={buckets[iso] || []}
              onStatusChange={onStatusChange}
              onToggle={onToggle}
            />
          );
        })}
      </div>
    </div>
  );
}

function LaneCell({ userId, dateKey, isToday, tasks, onStatusChange, onToggle }) {
  const { isOver, setNodeRef } = useDroppable({ id: cellId(userId, dateKey) });
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[70px] rounded-sm border ${isToday ? "border-[#F54800]/40 bg-orange-50/20" : "border-slate-100 bg-slate-50/30"} ${isOver ? "!bg-blue-50/50 !border-blue-300" : ""} p-1 space-y-1`}
      data-testid={`team-board-cell-${userId}-${dateKey}`}
    >
      {tasks.length === 0 ? (
        <div className="text-[9px] text-slate-300 italic text-center pt-3 select-none">·</div>
      ) : (
        tasks.map((t) => (
          <KanbanCard
            key={t.id}
            task={t}
            showAssignee={false}
            onStatusChange={(s) => onStatusChange(t, s)}
            onToggle={() => onToggle(t)}
          />
        ))
      )}
    </div>
  );
}
